import { BrowserModule } from '@angular/platform-browser';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NgModule } from '@angular/core';
import {
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

// Modules
import { Angulartics2Module } from 'angulartics2';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { environment } from '../environments/environment';
import { ActionReducer, MetaReducer } from '@ngrx/store';
import { localStorageSync } from 'ngrx-store-localstorage';
import { storeFreeze } from 'ngrx-store-freeze';

export function localStorageSyncReducer(
  reducer: ActionReducer<any>
): ActionReducer<any> {
  return localStorageSync({
    keys: ['newsData'],
    rehydrate: true
  })(reducer);
}

export const metaReducers: MetaReducer<any>[] = !environment.production
  ? [storeFreeze, localStorageSyncReducer]
  : [localStorageSyncReducer];

import { AppComponent } from './app.component';

import { routes } from './routes';

import { WebPlayerEffects } from './effects/web-player';
import { RouterEffects } from './effects/router';
import { AccountEffects } from './effects/accounts';
import { NewsEffects } from './effects/news.effects';

import { reducers } from './reducers';
import { services } from './services';

import { PageHomeComponent } from './routes/home/home.component';
import { PageFootballComponent } from './routes/football/football.component';
import { PageSoccerComponent } from './routes/soccer/soccer.component';
import { PageSupportComponent } from './routes/support/support.component';
import { PageSupportSoccerComponent } from './routes/support-soccer/support-soccer.component';
import { PageSupportFootballComponent } from './routes/support-football/support-football.component';
import { PagePressComponent } from './routes/press/press.component';
import { PagePrivacyPolicyComponent } from './routes/privacy-policy/privacy-policy.component';
import { PageTermsOfUseComponent } from './routes/terms-of-use/terms-of-use.component';
import { PageEulaComponent } from './routes/eula/eula.component';
import { PageNotFoundComponent } from './routes/not-found/not-found.component';
import { PageAccountsComponent } from './routes/page-accounts/page-accounts.component';
import { PageShareUnknownFootballComponent } from './routes/share-unknown-football/share-unknown-football.component';
import { PageShareUnknownSoccerComponent } from './routes/share-unknown-soccer/share-unknown-soccer.component';

import { GlobalHeaderComponent } from './components/header/header.component';
import { GlobalHeaderWebplayerComponent } from './components/header-webplayer/header-webplayer.component';
import { GlobalFooterComponent } from './components/footer/footer.component';
import { GlobalMobileMenuComponent } from './components/mobile-menu/mobile-menu.component';
import { GlobalHeroComponent } from './components/hero/hero.component';
import { GlobalPlatformsComponent } from './components/platforms/platforms.component';
import { GlobalTestamonialsComponent } from './components/testamonials/testamonials.component';
import { GlobalBottomComponent } from './components/bottom/bottom.component';
import { GlobalAppFeaturesComponent } from './components/app-features/app-features.component';
import { GlobalAdditionalFeaturesComponent } from './components/additional-features/additional-features.component';
import { GlobalAppDownloadsComponent } from './components/app-downloads/app-downloads.component';
import { GlobalVRDownloadsComponent } from './components/vr-downloads/vr-downloads.component';

import { HomeCarouselComponent } from './components/home/home-carousel.component';
import { HomeContentComponent } from './components/home/home-content.component';

import { FootballContentComponent } from './components/football/football-content.component';

import { SoccerContentComponent } from './components/soccer/soccer-content.component';

import { VRContentComponent } from './components/vr/vr-content.component';

import { SupportContentComponent } from './components/support/support-content.component';
import { SupportHeaderComponent } from './components/support/support-header.component';
import { YoutubeModalComponent } from './components/support/youtube-modal.component';

import { ShareUnknownFootballContentComponent } from './components/share-unknown-football/share-unknown-football-content.component';
import { ShareUnknownSoccerContentComponent } from './components/share-unknown-soccer/share-unknown-soccer-content.component';
import { PageShareAppleFootballComponent } from './routes/share-apple-football/share-apple-football.component';
import { PageShareAppleSoccerComponent } from './routes/share-apple-soccer/share-apple-soccer.component';
import { PageShareAndroidFootballComponent } from './routes/share-android-football/share-android-football.component';
import { PageShareAndroidSoccerComponent } from './routes/share-android-soccer/share-android-soccer.component';

import { ShareAndroidFootballContentComponent } from './components/share-android-football/share-android-football-content.component';
import { ShareAndroidSoccerContentComponent } from './components/share-android-soccer/share-android-soccer-content.component';

import { PressContentComponent } from './components/press/press-content.component';

import { PrivacyPolicyContentComponent } from './components/privacy-policy/privacy-policy-content.component';

import { TermsOfUseContentComponent } from './components/terms-of-use/terms-of-use-content.component';

import { EulaContentComponent } from './components/eula/eula-content.component';

import { NotfoundContentComponent } from './components/notfound/notfound-content.component';
import { ResetPasswordComponent } from './forms/reset-password/reset-password.component';
import { CreateUserComponent } from './forms/create-user/create-user.component';
import { VerifyComponent } from './forms/verify/verify.component';
import { VerifyUserComponent } from './forms/verify-user/verify-user.component';
import { CancelEmailChangeComponent } from './forms/cancel-email-change/cancel-email-change.component';
import { RevokeEmailChangeComponent } from './forms/revoke-email-change/revoke-email-change.component';
import { VoidEmailChangeComponent } from './forms/void-email-change/void-email-change.component';
import { VerifyEmailChangeComponent } from './forms/verify-email-change/verify-email-change.component';
import { ChangeEmailComponent } from './forms/change-email/change-email.component';
import { PasswordChangeComponent } from './forms/password-change/password-change.component';
import { VerifyPasswordChangeComponent } from './forms/verify-password-change/verify-password-change.component';
import { VerifyCancelPasswordChangeComponent } from './forms/verify-cancel-password-change/verify-cancel-password-change.component';
import { PasswordResetComponent } from './forms/password-reset/password-reset.component';
import { VerifyPasswordResetComponent } from './forms/verify-password-reset/verify-password-reset.component';
import { VerifyCancelPasswordResetComponent } from './forms/verify-cancel-password-reset/verify-cancel-password-reset.component';
import { UpdateEmailComponent } from './forms/update-email/update-email.component';
import { CancelEmailComponent } from './forms/cancel-email/cancel-email.component';
import { UnlockAccountComponent } from './forms/unlock-account/unlock-account.component';
import { ResendCodeComponent } from './forms/resend-code/resend-code.component';
import { NotificationComponent } from './components/notification/notification.component';
import { NotificationsContainerComponent } from './components/notifications-container/notifications-container.component';
import { TealiumUtagService } from './tealium/utag.service';
import { PageNewsComponent } from './routes/news/news.component';
import { ArticlesMainComponent } from './components/articles-main/articles-main.component';
import { PageNewsSingleComponent } from './routes/news-single/news-single.component';
import { ArticleComponent } from './components/article/article.component';
import { ShareAppleFootballContentComponent } from './components/share-apple-football/share-apple-football-content.component';
import { ShareAppleSoccerContentComponent } from './components/share-apple-soccer/share-apple-soccer-content.component';
import { PageVRComponent } from './routes/vr/vr.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { PageFitnessComponent } from './routes/fitness/fitness.component';
import { PageSupportFitnessComponent } from './routes/support-fitness/support-fitness.component';
import { FitnessContentComponent } from './components/fitness/fitness-content.component';

@NgModule({
  declarations: [
    AppComponent,
    PageHomeComponent,
    PageFootballComponent,
    PageSoccerComponent,
    PageVRComponent,
    PageSupportComponent,
    PageSupportSoccerComponent,
    PageSupportFootballComponent,
    PagePressComponent,
    PagePrivacyPolicyComponent,
    PageTermsOfUseComponent,
    PageEulaComponent,
    PageNotFoundComponent,
    PageShareUnknownFootballComponent,
    PageShareUnknownSoccerComponent,
    PageShareAppleFootballComponent,
    PageShareAppleSoccerComponent,
    PageShareAndroidFootballComponent,
    PageShareAndroidSoccerComponent,
    GlobalHeaderComponent,
    GlobalHeaderWebplayerComponent,
    GlobalFooterComponent,
    GlobalMobileMenuComponent,
    GlobalHeroComponent,
    GlobalPlatformsComponent,
    GlobalTestamonialsComponent,
    GlobalBottomComponent,
    GlobalAppFeaturesComponent,
    GlobalAdditionalFeaturesComponent,
    GlobalAppDownloadsComponent,
    GlobalVRDownloadsComponent,
    HomeCarouselComponent,
    HomeContentComponent,
    FootballContentComponent,
    SoccerContentComponent,
    VRContentComponent,
    SupportContentComponent,
    SupportHeaderComponent,
    YoutubeModalComponent,
    ShareUnknownFootballContentComponent,
    ShareUnknownSoccerContentComponent,
    ShareAppleFootballContentComponent,
    ShareAppleSoccerContentComponent,
    ShareAndroidFootballContentComponent,
    ShareAndroidSoccerContentComponent,
    PressContentComponent,
    PrivacyPolicyContentComponent,
    TermsOfUseContentComponent,
    EulaContentComponent,
    NotfoundContentComponent,
    ResetPasswordComponent,
    PageAccountsComponent,
    VerifyComponent,
    VerifyUserComponent,
    CancelEmailChangeComponent,
    RevokeEmailChangeComponent,
    VoidEmailChangeComponent,
    VerifyEmailChangeComponent,
    ChangeEmailComponent,
    VerifyCancelPasswordChangeComponent,
    VerifyPasswordChangeComponent,
    PasswordChangeComponent,
    PasswordResetComponent,
    VerifyPasswordResetComponent,
    VerifyCancelPasswordResetComponent,
    CreateUserComponent,
    UpdateEmailComponent,
    CancelEmailComponent,
    UnlockAccountComponent,
    ResendCodeComponent,
    NotificationComponent,
    NotificationsContainerComponent,
    PageNewsComponent,
    ArticlesMainComponent,
    PageNewsSingleComponent,
    ArticleComponent,
    PageFitnessComponent,
    PageSupportFitnessComponent,
    FitnessContentComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatDialogModule,
    MatButtonModule,
    FlexLayoutModule,
    RouterModule.forRoot(routes),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    !environment.production
      ? StoreDevtoolsModule.instrument({ maxAge: 50, connectInZone: true })
      : [],
    EffectsModule.forRoot([
      WebPlayerEffects,
      RouterEffects,
      AccountEffects,
      NewsEffects
    ]),
    Angulartics2Module.forRoot()
  ],
  providers: [
    services,
    { provide: TealiumUtagService, useClass: TealiumUtagService },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
